import React from 'react';
import { Star } from 'lucide-react';
import { useLanguage } from '../../../language/hooks/useLanguageHook';
import { dashboardPreviewKeys } from '../../data/landingPageData'; // Import keys for translation

const DashboardPreviewSection = () => {
  const { t } = useLanguage();
  const keys = dashboardPreviewKeys; // Alias for brevity

  return (
    <div className="relative z-10 bg-white py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 scroll-fade-in">
          <h2 className="text-4xl font-bold mb-4">
            {t("public.landing.dashboard.title")}
          </h2>
          <p className="text-xl text-gray-600">
            {t("public.landing.dashboard.subtitle")}
          </p>
        </div>

        <div className="relative">
          {/* Dashboard Preview */}
          <div className="rounded-xl shadow-2xl overflow-hidden scroll-scale-in border border-gray-200"> {/* Added subtle border */}
            {/* Window Controls Bar */}
            <div className="bg-gray-100 px-4 py-3 flex items-center justify-between border-b border-gray-200"> {/* Lighter bar */}
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 rounded-full bg-red-400" />
                <div className="w-3 h-3 rounded-full bg-yellow-400" />
                <div className="w-3 h-3 rounded-full bg-green-400" />
              </div>
              <div className="text-gray-500 text-sm font-medium">
                {t(keys.title)}
              </div>
              <div className="w-12"></div> {/* Spacer */}
            </div>

            {/* Dashboard Content */}
            <div className="grid grid-cols-12 gap-6 bg-gray-50 p-6">
              {/* Sidebar Placeholder */}
              <div className="col-span-3 bg-white rounded-lg p-4 shadow-sm">
                <div className="space-y-4 animate-pulse">
                  <div className="h-8 bg-gray-200 rounded w-full" />
                  <div className="h-8 bg-blue-100 rounded w-3/4" />
                  <div className="h-8 bg-gray-200 rounded w-full" />
                  <div className="h-8 bg-gray-200 rounded w-5/6" />
                </div>
              </div>

              {/* Main Content Placeholder */}
              <div className="col-span-9 space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-6">
                  {/* Reviews Today Card */}
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">
                      {t(keys.stats.reviewsToday.label)}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {t(keys.stats.reviewsToday.value)}
                    </div>
                    <div className="text-sm text-green-600 font-medium mt-1">
                      {t(keys.stats.reviewsToday.trend)}
                    </div>
                  </div>
                  {/* Response Rate Card */}
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">
                      {t(keys.stats.responseRate.label)}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {t(keys.stats.responseRate.value)}
                    </div>
                    <div className="text-sm text-green-600 font-medium mt-1">
                      {t(keys.stats.responseRate.trend)}
                    </div>
                  </div>
                  {/* Avg Rating Card */}
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <div className="text-sm text-gray-600 mb-1">
                      {t(keys.stats.avgRating.label)}
                    </div>
                    <div className="text-2xl font-bold text-gray-900">
                      {t(keys.stats.avgRating.value)}
                    </div>
                    <div className="text-sm text-green-600 font-medium mt-1">
                      {t(keys.stats.avgRating.trend)}
                    </div>
                  </div>
                </div>

                {/* Review List Placeholder */}
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="space-y-6">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-start space-x-4 pb-4 border-b border-gray-100 last:border-b-0 animate-pulse">
                        <div className="w-10 h-10 rounded-full bg-gray-200" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-200 rounded w-1/4" />
                          <div className="h-4 bg-gray-200 rounded w-3/4" />
                          <div className="flex items-center space-x-1">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star key={star} className="h-4 w-4 text-gray-300 fill-current" />
                            ))}
                          </div>
                        </div>
                        <div className="w-24 md:w-32">
                          <div className="h-8 bg-blue-100 rounded" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Feature Callouts */}
          <div className="absolute -right-12 top-1/4 transform translate-x-1/2 hidden md:block"> {/* Hide on small screens */}
            <div className="bg-blue-600 text-white px-5 py-2 rounded-lg shadow-lg text-sm font-medium">
              {t(keys.features.analytics)}
            </div>
          </div>
          <div className="absolute -left-12 top-2/3 transform -translate-x-1/2 hidden md:block"> {/* Hide on small screens */}
            <div className="bg-green-600 text-white px-5 py-2 rounded-lg shadow-lg text-sm font-medium">
              {t(keys.features.response)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPreviewSection; 